import { Effect, Option, Schema } from 'effect';
import { CloudStorageProviderSchema } from './integration-providers';

export const GenericFailure = Schema.TaggedStruct('Generic', {});
export const DispatchFailedFailure = Schema.TaggedStruct('DispatchFailed', {});
export const NoReadableTextFailure = Schema.TaggedStruct('NoReadableText', {});
export const DrmProtectedFailure = Schema.TaggedStruct('DrmProtected', {});
export const TooManyPagesFailure = Schema.TaggedStruct('TooManyPages', {
  page_count: Schema.Int,
  max_pages: Schema.Int,
});
export const UnsupportedLanguageFailure = Schema.TaggedStruct(
  'UnsupportedLanguage',
  { language: Schema.String },
);
export const WebPageEmptyFailure = Schema.TaggedStruct('WebPageEmpty', {});
export const WebPageForbiddenFailure = Schema.TaggedStruct(
  'WebPageForbidden',
  {},
);
export const WebPageBlockedFailure = Schema.TaggedStruct('WebPageBlocked', {});
export const WebPageUnreachableFailure = Schema.TaggedStruct(
  'WebPageUnreachable',
  {},
);
export const PdfInvalidFailure = Schema.TaggedStruct('PdfInvalid', {});
export const PdfPasswordProtectedFailure = Schema.TaggedStruct(
  'PdfPasswordProtected',
  {},
);
export const CostExceededFailure = Schema.TaggedStruct('CostExceeded', {});
export const TooManyAttemptsFailure = Schema.TaggedStruct(
  'TooManyAttempts',
  {},
);
export const DocumentTooLargeFailure = Schema.TaggedStruct('DocumentTooLarge', {
  size_bytes: Schema.Int,
  max_bytes: Schema.Int,
});
export const DocumentTypeUnsupportedFailure = Schema.TaggedStruct(
  'DocumentTypeUnsupported',
  {},
);
export const SourceReconnectRequiredFailure = Schema.TaggedStruct(
  'SourceReconnectRequired',
  { source: CloudStorageProviderSchema },
);
export const SourceFileUnavailableFailure = Schema.TaggedStruct(
  'SourceFileUnavailable',
  { source: CloudStorageProviderSchema },
);
export const SourceUnavailableFailure = Schema.TaggedStruct(
  'SourceUnavailable',
  { source: CloudStorageProviderSchema },
);

/**
 * Why a conversion failed, as the user should be told: one member per
 * user-facing message, carrying the values that message needs, and named for
 * what happened to the document rather than where it came from. Stored in
 * `audio.error_detail` and returned on every conversion summary.
 */
export const ConversionFailure = Schema.Union([
  GenericFailure,
  DispatchFailedFailure,
  NoReadableTextFailure,
  DrmProtectedFailure,
  TooManyPagesFailure,
  UnsupportedLanguageFailure,
  WebPageEmptyFailure,
  WebPageForbiddenFailure,
  WebPageBlockedFailure,
  WebPageUnreachableFailure,
  PdfInvalidFailure,
  PdfPasswordProtectedFailure,
  CostExceededFailure,
  TooManyAttemptsFailure,
  DocumentTooLargeFailure,
  DocumentTypeUnsupportedFailure,
  SourceReconnectRequiredFailure,
  SourceFileUnavailableFailure,
  SourceUnavailableFailure,
]);
export type ConversionFailure = typeof ConversionFailure.Type;

/**
 * The wire field. A failure this client does not know yet (added by a newer
 * server) decodes to null, so the client shows the stored English message
 * instead of failing to decode the whole response.
 */
export const ConversionFailureField = Schema.NullOr(ConversionFailure).pipe(
  Schema.catchDecoding(() => Effect.succeedSome(null)),
);

export const decodeStoredConversionFailure = (stored: unknown) =>
  Option.getOrNull(Schema.decodeUnknownOption(ConversionFailure)(stored));
